import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle, CheckCircle2, UserX, XCircle, ChevronDown, ChevronRight, ChevronLeft,
  MapPin, Video, RotateCcw, DollarSign,
} from 'lucide-react';
import ConfirmarStatusModal from './ConfirmarStatusModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIPO_CFG = {
  presencial: { icon: MapPin,    color: 'text-indigo-700',  bg: 'bg-indigo-50',   label: 'Presencial' },
  online:     { icon: Video,     color: 'text-cyan-700',    bg: 'bg-cyan-50',     label: 'Online' },
  retorno:    { icon: RotateCcw, color: 'text-purple-700',    bg: 'bg-purple-50',     label: 'Retorno' },
};

const TipoTag = ({ tipoConsulta, modoAtendimento }) => {
  let key = 'presencial';
  if (tipoConsulta === 'retorno') key = 'retorno';
  else if (modoAtendimento === 'online') key = 'online';
  const cfg = TIPO_CFG[key];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${cfg.bg} ${cfg.color}`}>
      <Icon className="w-2.5 h-2.5" strokeWidth={2.2}/>
      {cfg.label}
    </span>
  );
};

const Avatar = ({ name }) => {
  const initials = (name || '?').split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();
  return (
    <div className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-xs flex-shrink-0 bg-amber-100 text-amber-800">
      {initials}
    </div>
  );
};

const formatDataBR = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

export default function PendenciasStatusBanner({ onCancelarConsulta, onChange, onAbrirFinanceiro }) {
  const [pendentes, setPendentes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [requested, setRequested] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [modalState, setModalState] = useState({ open: false, action: null, consulta: null });
  const [savingId, setSavingId] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 4;

  const fetchPendentes = useCallback(async () => {
    setRequested(true);
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/consultas/pendentes-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendentes(res.data.consultas || []);
    } catch (err) {
      // Silencioso — não bloqueia agenda
      setPendentes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset page if it goes out of range (after deletions)
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(pendentes.length / PAGE_SIZE));
    if (page > totalPages) setPage(totalPages);
  }, [pendentes.length, page]);

  const refresh = useCallback(async () => {
    await fetchPendentes();
    if (onChange) onChange();
  }, [fetchPendentes, onChange]);

  const handleQuickAction = (action, consulta) => {
    if (action === 'cancelar') {
      // Reusa o modal de cancelamento existente
      if (onCancelarConsulta) {
        onCancelarConsulta(consulta, refresh);
      }
      return;
    }
    setModalState({ open: true, action, consulta });
  };

  const handleConfirmStatus = async () => {
    const { action, consulta } = modalState;
    if (!consulta || !action) return;
    setSavingId(consulta.id);
    try {
      const token = localStorage.getItem('token');
      const novoStatus = action === 'realizada' ? 'realizada' : 'falta';
      await axios.put(
        `${API}/consultas/${consulta.id}`,
        { status: novoStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(action === 'realizada' ? 'Consulta marcada como Realizada' : 'Consulta marcada como Falta');
      setModalState({ open: false, action: null, consulta: null });
      await refresh();
    } catch (err) {
      toast.error('Erro ao atualizar status: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setSavingId(null);
    }
  };

  const handleHide = null; // removed by request

  if (!requested) {
    return (
      <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 flex items-center justify-between gap-3" data-testid="pendencias-on-demand-panel">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span className="text-sm font-semibold text-amber-900 truncate">Pendências de Consultas</span>
        </div>
        <button
          type="button"
          onClick={fetchPendentes}
          className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold transition-colors flex-shrink-0"
          data-testid="btn-ver-pendencias"
        >
          Ver pendências
        </button>
      </div>
    );
  }

  if (loading && pendentes.length === 0) {
    return (
      <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 flex items-center gap-3" data-testid="pendencias-loading-panel">
        <div className="w-4 h-4 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
        <span className="text-sm font-semibold text-amber-900">Carregando pendências...</span>
      </div>
    );
  }

  if (!loading && pendentes.length === 0) {
    return (
      <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 flex items-center justify-between gap-3" data-testid="pendencias-empty-panel">
        <div className="flex items-center gap-2 min-w-0">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <span className="text-sm font-semibold text-emerald-900 truncate">Nenhuma pendência encontrada</span>
        </div>
        <button
          type="button"
          onClick={fetchPendentes}
          className="px-3 py-1.5 rounded-lg bg-white border border-emerald-200 hover:bg-emerald-100 text-emerald-800 text-xs font-bold transition-colors flex-shrink-0"
          data-testid="btn-atualizar-pendencias"
        >
          Atualizar
        </button>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(pendentes.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const endIdx = startIdx + PAGE_SIZE;
  const visiveis = pendentes.slice(startIdx, endIdx);
  const hasMultiplePages = totalPages > 1;
  const totalAcimaDe7Dias = pendentes.filter(p => (p.dias_atras ?? 0) > 7).length;
  // unused but keep for future ref
  void handleHide;

  return (
    <>
      <div
        className="mb-4 rounded-xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 overflow-hidden shadow-sm"
        data-testid="banner-pendencias"
      >
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full px-4 sm:px-5 py-3 flex items-center justify-between hover:bg-amber-100/40 transition-colors"
          data-testid="banner-pendencias-toggle"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0 shadow-sm ring-4 ring-amber-200/60">
              <AlertTriangle className="w-5 h-5 text-white" strokeWidth={2.2}/>
            </div>
            <div className="text-left min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold text-amber-900">
                  Pendência de Consultas · {pendentes.length} {pendentes.length === 1 ? 'consulta' : 'consultas'}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold uppercase tracking-wider">Pendentes</span>
                {totalAcimaDe7Dias > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-[10px] font-bold uppercase tracking-wider">
                    {totalAcimaDe7Dias} há mais de 7 dias
                  </span>
                )}
              </div>
              <div className="text-[12px] text-amber-800 mt-0.5 hidden sm:block">
                Resolva pendências de status (Realizada/Falta/Cancelada) e financeiras (consultas com pagamento incompleto).
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            <span className="text-[11px] font-semibold text-amber-700 hidden sm:inline">{expanded ? 'Recolher' : 'Ver consultas'}</span>
            {expanded ? <ChevronDown className="w-4 h-4 text-amber-700"/> : <ChevronRight className="w-4 h-4 text-amber-700"/>}
          </div>
        </button>

        {expanded && (
          <div className="border-t border-amber-200 bg-white">
            {visiveis.map((c, i) => {
              const isLast = i === visiveis.length - 1;
              const isSaving = savingId === c.id;
              return (
                <div
                  key={c.id}
                  className={`px-4 sm:px-5 py-3 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4 hover:bg-amber-50/50 transition-colors ${!isLast ? 'border-b border-gray-100' : ''} ${isSaving ? 'opacity-50' : ''}`}
                  data-testid={`pendencia-item-${c.id}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Avatar name={c.paciente_nome}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-bold text-gray-900 truncate mb-0.5">{c.paciente_nome || 'Paciente'}</div>
                      <div className="text-[11px] text-gray-600 flex items-center gap-2 flex-wrap">
                        <span className="truncate">{c.categoria || 'Consulta'}</span>
                        <span className="w-1 h-1 rounded-full bg-gray-300 flex-shrink-0"/>
                        <span className="font-mono tabular-nums whitespace-nowrap">{formatDataBR(c.data_consulta)} · {c.hora_inicio}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
                    {(c.pendencias || []).includes('status') && (
                      <>
                        <button
                          onClick={() => handleQuickAction('realizada', c)}
                          disabled={isSaving}
                          className="h-8 px-2.5 text-[11px] rounded-md font-semibold inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white shadow-sm transition-colors"
                          data-testid={`pendencia-${c.id}-btn-realizada`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.2}/>
                          <span className="hidden sm:inline">Realizada</span>
                        </button>
                        <button
                          onClick={() => handleQuickAction('falta', c)}
                          disabled={isSaving}
                          className="h-8 px-2.5 text-[11px] rounded-md font-semibold inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white shadow-sm transition-colors"
                          data-testid={`pendencia-${c.id}-btn-falta`}
                        >
                          <UserX className="w-3.5 h-3.5" strokeWidth={2.2}/>
                          <span className="hidden sm:inline">Falta</span>
                        </button>
                        <button
                          onClick={() => handleQuickAction('cancelar', c)}
                          disabled={isSaving}
                          className="h-8 px-2.5 text-[11px] rounded-md font-semibold inline-flex items-center gap-1 bg-white border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                      data-testid={`pendencia-${c.id}-btn-cancelar`}
                    >
                      <XCircle className="w-3.5 h-3.5" strokeWidth={2.2}/>
                      <span className="hidden sm:inline">Cancelar</span>
                    </button>
                      </>
                    )}
                    {(c.pendencias || []).includes('financeiro') && (
                      <button
                        onClick={() => onAbrirFinanceiro && onAbrirFinanceiro(c, refresh)}
                        disabled={isSaving}
                        className="h-8 px-2.5 text-[11px] rounded-md font-semibold inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white shadow-sm transition-colors"
                        data-testid={`pendencia-${c.id}-btn-financeiro`}
                      >
                        <DollarSign className="w-3.5 h-3.5" strokeWidth={2.2}/>
                        <span className="hidden sm:inline">Inserir Pagamento</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            <div className="px-4 sm:px-5 py-2.5 bg-amber-50/60 border-t border-amber-100 flex items-center justify-between flex-wrap gap-2"
              data-testid="banner-pendencias-footer">
              <div className="flex items-center gap-2 text-[11px] text-amber-900">
                <span className="font-mono tabular-nums">
                  Exibindo <strong>{startIdx + 1}–{Math.min(endIdx, pendentes.length)}</strong> de <strong>{pendentes.length}</strong>
                </span>
                {hasMultiplePages && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold uppercase tracking-wider"
                    data-testid="banner-pendencias-pagecount">
                    {totalPages} {totalPages === 1 ? 'página' : 'páginas'}
                  </span>
                )}
              </div>

              {hasMultiplePages && (
                <div className="flex items-center gap-1.5" data-testid="banner-pendencias-pagination">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-amber-300 bg-white text-amber-800 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Página anterior"
                    data-testid="banner-page-prev"
                  >
                    <ChevronLeft className="w-4 h-4" strokeWidth={2}/>
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => {
                      const isCurrent = n === safePage;
                      // Compact: show first, last, current, and neighbors
                      const shouldShow = n === 1 || n === totalPages || Math.abs(n - safePage) <= 1;
                      const isEllipsis = !shouldShow && (n === 2 || n === totalPages - 1);
                      if (!shouldShow && !isEllipsis) return null;
                      if (isEllipsis) {
                        return <span key={`e-${n}`} className="px-1 text-amber-700 text-[11px] font-bold">…</span>;
                      }
                      return (
                        <button
                          key={n}
                          onClick={() => setPage(n)}
                          className={`min-w-[28px] h-7 px-2 rounded-md text-[11px] font-bold tabular-nums transition-colors ${
                            isCurrent
                              ? 'bg-amber-500 text-white shadow-sm'
                              : 'bg-white border border-amber-300 text-amber-800 hover:bg-amber-100'
                          }`}
                          data-testid={`banner-page-${n}`}
                          aria-current={isCurrent ? 'page' : undefined}
                        >
                          {n}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-amber-300 bg-white text-amber-800 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Próxima página"
                    data-testid="banner-page-next"
                  >
                    <ChevronRight className="w-4 h-4" strokeWidth={2}/>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <ConfirmarStatusModal
        open={modalState.open}
        onOpenChange={(open) => !open && setModalState({ open: false, action: null, consulta: null })}
        action={modalState.action}
        consulta={modalState.consulta}
        onConfirm={handleConfirmStatus}
        loading={savingId !== null}
      />
    </>
  );
}
